import { ComponentRef } from '@angular/core';
import { TemplateContext } from '../../../../models/reportTemplate.model';
import { EditorPropertySchema, EditorRenderStrategy, WidgetPropertyType, parseAttribute } from '../widget-config.helpers';
import { GravimetricCardTemplate, GravimetricWidgetComponent, RiskLevel, SampleGravimetricData } from './gravimetric-widget.component';

export enum GravimetricWidgetAttr {
    ShowHeader = 'showHeader',
    CardTemplate = 'cardTemplate'
}

interface GravimetricContext extends TemplateContext {
    riskLevels?: RiskLevel[];
    jurisdictionRiskLevelsMap?: Record<number, RiskLevel[]>;
    sample?: { isVoid?: boolean; voidReason?: string };
    computedData?: { gravimetricData?: SampleGravimetricData };
}

function isGravimetricCardTemplate(value: string): value is GravimetricCardTemplate {
    return value === GravimetricCardTemplate.Default || value === GravimetricCardTemplate.Compact;
}

export function configureGravimetricWidget(
    componentRef: ComponentRef<unknown>,
    context: TemplateContext,
    sourceElement?: HTMLElement
): void {
    const component = componentRef.instance as GravimetricWidgetComponent;
    const ctx = context as GravimetricContext;

    component.data = ctx.computedData?.gravimetricData ?? null;
    component.showHeader = parseAttribute(sourceElement?.getAttribute(GravimetricWidgetAttr.ShowHeader), true);
    const cardTemplateAttr = sourceElement?.getAttribute(GravimetricWidgetAttr.CardTemplate);
    component.cardTemplate = cardTemplateAttr && isGravimetricCardTemplate(cardTemplateAttr) ? cardTemplateAttr : GravimetricCardTemplate.Default;
    component.riskLevels = ctx.riskLevels || [];
    component.jurisdictionRiskLevelsInput = ctx.jurisdictionRiskLevelsMap || {};
    component.isVoid = !!(ctx.sample?.isVoid);
    component.voidReason = ctx.sample?.voidReason || '';
}

export const gravimetricEditorPropertySchema: EditorPropertySchema = {
    renderStrategy: EditorRenderStrategy.ReadOnly,
    propertySchema: [
        { key: GravimetricWidgetAttr.ShowHeader, label: 'Show Header', type: WidgetPropertyType.Boolean },
        { key: GravimetricWidgetAttr.CardTemplate, label: 'Card Template', type: WidgetPropertyType.String }
    ]
};

export const gravimetricLoopTemplate = [
    '[[#if sample.isVoid]]',
    '<div class="void-card" style="padding:16px;border:1px solid #e5e7eb;border-radius:8px;text-align:center;">',
    '<h3 class="void-title" style="margin:0;color:#374151;">Sample Voided</h3>',
    '[[#if sample.voidReason]]<p class="void-reason" style="margin:8px 0 0 0;color:#6b7280;"><strong>Void Reason:</strong> [[sample.voidReason]]</p>[[/if]]',
    '</div>',
    '[[/if]]',
    '[[#if !sample.isVoid]]',
    '<div class="gravimetric-widget-container no-page-break">',
    '[[#if computedData.gravimetricDuration]]',
    '<div class="widget-header no-page-break" style="display:flex;justify-content:flex-end;align-items:center;gap:6px;margin-bottom:10px;">',
    '<span class="duration-label" style="font-size:13px;color:#6b7280;">[[computedData.gravimetricDurationLabel]]:</span>',
    '<span class="duration-value" style="font-size:13px;font-weight:600;color:#374151;">[[computedData.gravimetricDuration]]</span>',
    '</div>',
    '[[/if]]',
    '[[#each computedData.gravimetricGroups]]',
    '<div class="jurisdiction-box no-page-break" style="margin-bottom:20px;padding:12px 15px;border:1px solid #e5e7eb;border-radius:8px;page-break-inside:avoid;break-inside:avoid;">',
    '<div class="jurisdiction-box-header" style="font-size:13px;font-weight:600;color:#374151;margin-bottom:10px;padding-bottom:6px;border-bottom:1px solid #e5e7eb;">',
    '[[this.jurisdictionLabel]]: [[this.jurisdictionName]]',
    '</div>',
    '<div class="agent-cards-grid" style="font-size:0;display:block;">',
    '[[#each this.agents]]',
    '<div class="agent-card no-page-break" data-exposure="[[this.exposureLevel]]" data-tpl-style="border-color:[[this.maxExposureColor]];border-left-color:[[this.maxExposureColor]]" style="border:1px solid #e5e7eb;border-left:6px solid #e5e7eb;margin:8px 15px 8px 0;display:inline-block;width:calc(50% - 15px);min-height:160px;vertical-align:top;font-size:14px;padding:12px 10px 10px 10px;page-break-inside:avoid;break-inside:avoid;box-sizing:border-box;position:relative;overflow:visible;">',
    '<div class="agent-header" style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:10px;"><div class="agent-info" style="flex:1;">',
    '<span class="agent-label" style="display:block;font-size:10px;color:#dbaf40;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:3px;">[[this.agentNameUpper]]</span>',
    '<h3 class="agent-name" style="margin:0 0 8px 0;font-size:14px;font-weight:600;color:#333333;line-height:1.2;">[[this.agentName]]</h3>',
    '</div></div>',
    '<div class="jurisdictions-section">',
    '[[#each this.jurisdictions]]',
    '<div class="jurisdiction-item" style="margin-bottom:12px;">',
    '[[#if this.isLoq]]',
    '<div class="twa-section"><div class="twa-header">',
    '<span class="twa-value" data-tpl-style="color:[[this.exposureColor]]" style="font-size:16px;font-weight:700;">[[this.loqDisplayText]]</span>',
    '</div></div>',
    '[[/if]]',
    '[[#if !this.isLoq]]',
    '<div class="oel-percentage-header" style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;padding-bottom:8px;border-bottom:2px solid #e9ecef;">',
    '<div class="oel-percentage-label" style="font-size:10px;color:#dbaf40;font-weight:600;text-transform:uppercase;letter-spacing:0.5px;">[[this.percentageLabel]]</div>',
    '<div class="oel-percentage-value" data-tpl-style="color:[[this.exposureColor]]" style="font-size:14px;font-weight:700;">[[this.percentageDisplay]]%</div>',
    '</div>',
    '<div class="twa-section">',
    '<div class="twa-header" style="display:flex;justify-content:space-between;align-items:baseline;margin-bottom:6px;">',
    '<div class="twa-value" style="font-size:22px;font-weight:700;color:#333333;"><span data-tpl-if="this.showCensorSymbol" class="censor-symbol" style="color:#dc3545;font-weight:700;">[[this.censorSymbol]]</span>[[this.twaDisplay]]</div>',
    '<div class="twa-unit" style="margin-left:4px;font-size:11px;color:#666666;font-weight:500;">[[this.twaUnit]]</div>',
    '</div>',
    '<div class="progress-container" style="position:relative;margin:6px 0;">',
    '<div class="progress-bar" style="position:relative;height:8px;background:#f3f4f6;border-radius:4px;overflow:visible;">',
    '<div class="progress-fill" data-tpl-style="width:[[this.progressWidth]]%;background-color:[[this.exposureColor]]" style="height:100%;border-radius:4px;"></div>',
    '<div class="oel-marker" data-tpl-style="left:[[this.oelPosition]]%" style="position:absolute;top:-2px;width:2px;height:12px;background:#111827;"></div>',
    '</div>',
    '<div class="progress-dashed-line"></div>',
    '</div>',
    '<div class="bottom-info" style="display:flex;justify-content:flex-start;font-size:10px;color:#666666;margin-top:4px;">',
    '<span class="oel-info" style="font-weight:500;color:#666666;white-space:nowrap;">[[this.oelLineDisplay]]</span>',
    '</div>',
    '</div>',
    '[[/if]]',
    '</div>',
    '[[/each]]',
    '</div>',
    '</div>',
    '[[/each]]',
    '</div>',
    '[[#if this.hasLegend]]',
    '<div class="risk-legend-container" style="display:flex;align-items:center;gap:10px;margin-top:8px;padding-top:6px;border-top:1px solid #e5e7eb;">',
    '<span class="legend-title" style="font-size:11px;color:#828282;font-weight:600;margin-right:4px;">[[this.legendTitle]]</span>',
    '<div class="legend-items" style="display:flex;flex-wrap:wrap;gap:8px;margin-top:4px;">',
    '[[#each this.legendItems]]',
    '<div class="legend-item" style="display:flex;align-items:center;gap:4px;">',
    '<span class="legend-color" data-tpl-style="background-color:[[this.color]]" style="display:inline-block;width:12px;height:12px;border-radius:2px;">&#8203;</span>',
    '<span class="legend-text" style="font-size:11px;color:#374151;">[[this.text]]</span>',
    '</div>',
    '[[/each]]',
    '</div>',
    '</div>',
    '[[/if]]',
    '</div>',
    '[[/each]]',
    '</div>',
    '[[/if]]'
].join('');
